import { HttpErrorResponse } from '@angular/common/http';
import { StateContext } from '@ngxs/store';
import { IActionWithPayload } from 'app/projects/core/src/lib/interfaces/action_with_payload.interface';
import { MaxBrainUtils } from 'app/projects/core/src/lib/utils';
import { merge, Observable } from 'rxjs';
import { reduce, takeLast } from 'rxjs/operators';
import { IEntity } from '../interfaces/entity.interface';
import { IEntityApiService } from '../interfaces/entity.service.interface';
import { IFetchEntityFailureActionPayload } from '../interfaces/fetch-entity-failure-payload.interface';
import { EntityStateModel } from './entity.state-model';

export abstract class EntityState<T extends IEntity> {
    private _expirationTime = {};
    protected _propstoSearch = null;

    constructor(protected entityApiService: IEntityApiService<T>, private _timeoutDuration: number = 0) {
        if (this._timeoutDuration < 0) this._timeoutDuration = 0;
    }

    /**
     *
     * @param key
     */
    protected setExpirationTime(key: string): void {
        this._expirationTime[key] = this._timeoutDuration * 1000 + Date.now();
    }

    /** Clears fetch short-cache so the next Fetch* for this id performs a real HTTP read (see _fetchEntity expiration branch). */
    protected clearFetchCacheEntry(id: string): void {
        if (id) {
            delete this._expirationTime[id];
        }
    }

    /**
     *
     * @param entities
     * @param searchString
     */
    protected _filterEntitiesByStringAndMapToIds(entities: T[], searchString: string): string[] {
        return MaxBrainUtils.filterArrayByString(entities, searchString, this._propstoSearch).map((entity) => entity.id);
    }

    /**
     *
     * @param ctx
     * @param payload
     * @param AddEntitiesAction
     */
    protected _setEntity<M extends EntityStateModel<T>>(ctx: StateContext<M>, { payload }: IActionWithPayload<T>, AddEntitiesAction: any = null): void {
        ctx.patchState({ item: payload.id } as Partial<M>);

        if (!!AddEntitiesAction) {
            // TODO: maybe move in front of patchState
            ctx.dispatch(new AddEntitiesAction([payload]));
        }
    }

    /**
     *
     * @param patchState
     */
    protected _unsetEntity<M extends EntityStateModel<T>>({ patchState }: StateContext<M>): void {
        patchState({ item: null } as Partial<M>);
    }

    /**
     *
     * @param ctx
     * @param payload
     */
    protected _addEntities<M extends EntityStateModel<T>>(ctx: StateContext<M>, { payload }: IActionWithPayload<T[]>): void {
        const state = ctx.getState();
        payload = payload ? payload.filter((entity) => !!entity) : [];

        const stateList = state.map ? Array.from(state.map.values()) : null;
        let entities: T[];

        if (!stateList) {
            entities = [...payload];
        } else {
            const payloadIds = payload.map((item) => item.id);
            entities = [...stateList.filter((entity) => !payloadIds.includes(entity.id)), ...payload];
        }

        this._setEntities(ctx, { payload: entities });
    }

    /**
     *
     * @param ctx
     * @param payload
     * @param SuccessAction
     * @param FailureAction
     * @param AddEntitiesAction
     * @param SetEntityAction
     * @param serviceMethod
     */
    protected async _createEntity<M extends EntityStateModel<T>>(
        ctx: StateContext<M>,
        { payload }: IActionWithPayload<T>,
        SuccessAction: any,
        FailureAction: any,
        AddEntitiesAction: any,
        SetEntityAction: any = null,
        serviceMethod: Observable<T> = this.entityApiService ? this.entityApiService.create(payload) : null
    ): Promise<T> {
        if (!serviceMethod) throw new Error('A service method must be provided to use the _createEntity protected method. ');

        try {
            const entity = await serviceMethod.toPromise();
            this.setExpirationTime(entity.id);

            const actions = [];
            let setEntity = !!SetEntityAction;

            if (setEntity) {
                for (const key in payload) {
                    if (key !== 'id' && entity[key] !== payload[key]) {
                        setEntity = false;
                    }
                }
            }

            if (setEntity) actions.push(new SetEntityAction(entity));
            else if (!AddEntitiesAction) throw new Error('Either AddEntitiesAction or SetEntityAction property must be defined!');
            else actions.push(new AddEntitiesAction([entity]));

            setTimeout(() => {
                ctx.dispatch([...actions, new SuccessAction(entity)]);
            });

            return entity;
        } catch (e) {
            if (e instanceof HttpErrorResponse) ctx.dispatch(new FailureAction({ response: e }));
            else throw new Error(e);
        }
    }

    /**
     *
     * @param ctx
     * @param payload
     * @param SuccessAction
     * @param FailureAction
     * @param AddEntitiesAction
     * @param serviceMethod
     */
    protected async _updateEntity<M extends EntityStateModel<T>>(
        ctx: StateContext<M>,
        { payload }: IActionWithPayload<T>,
        SuccessAction: any,
        FailureAction: any,
        AddEntitiesAction: any = null,
        serviceMethod: Observable<T> = this.entityApiService ? this.entityApiService.update(payload) : null
    ): Promise<T> {
        if (!serviceMethod) throw new Error('A service method must be provided to use the _updateEntity protected method. ');

        try {
            const entity = await serviceMethod.toPromise();
            this.setExpirationTime(entity.id);

            const actions = [];
            if (!!AddEntitiesAction) actions.push(new AddEntitiesAction([entity]));

            ctx.dispatch([...actions, new SuccessAction(entity)]);
            return entity;
        } catch (e) {
            if (e instanceof HttpErrorResponse) ctx.dispatch(new FailureAction(payload.id));
            else throw new Error(e);
        }
    }

    /**
     *
     * @param ctx
     * @param payload
     * @param SuccessAction
     * @param FailureAction
     * @param serviceMethod
     */
    protected async _deleteEntity<M extends EntityStateModel<T>>(
        ctx: StateContext<M>,
        { payload }: IActionWithPayload<string>,
        SuccessAction: any,
        FailureAction: any,
        serviceMethod: Observable<string> = this.entityApiService ? this.entityApiService.delete(payload) : null
    ): Promise<boolean> {
        if (!serviceMethod) throw new Error('A service method must be provided to use the _deleteEntity protected method. ');

        try {
            await serviceMethod.toPromise();
            this._removeEntitiesFromState(ctx, [payload]);
            ctx.dispatch(new SuccessAction(payload));
            return true;
        } catch (e) {
            if (e instanceof HttpErrorResponse) ctx.dispatch(new FailureAction(payload));
            else throw new Error(e);
            return false;
        }
    }

    /**
     *
     * @param ctx
     * @param payload
     * @param SuccessAction
     * @param FailureAction
     * @param AddEntitiesAction
     * @param SetEntityAction
     * @param serviceMethod
     */
    protected async _fetchEntity<M extends EntityStateModel<T>>(
        ctx: StateContext<M>,
        { payload }: IActionWithPayload<string>,
        SuccessAction: any,
        FailureAction: any,
        AddEntitiesAction: any,
        SetEntityAction: any = null,
        serviceMethod: Observable<T> = this.entityApiService ? this.entityApiService.read(payload) : null
    ): Promise<T> {
        if (!serviceMethod) throw new Error('A service method must be provided to use the _fetchEntity protected method. ');

        const state = ctx.getState();

        if (state.fetchingItem.includes(payload)) return;

        ctx.patchState({ fetchingItem: [...state.fetchingItem, payload] } as Partial<M>);
        const actions = [];

        if (this._expirationTime[payload] && this._expirationTime[payload] > Date.now()) {
            let entity = state.map ? state.map.get(payload) : null;

            if (entity) {
                setTimeout(() => {
                    if (SetEntityAction) actions.push(new SetEntityAction(entity));
                    ctx.dispatch([...actions, new SuccessAction(entity)]);
                });
                return entity;
            }
        }

        try {
            const entity = await serviceMethod.toPromise();

            if (SetEntityAction) actions.push(new SetEntityAction(entity));
            else if (!AddEntitiesAction) throw new Error('Either AddEntitiesAction or SetEntityAction property must be defined!');
            else actions.push(new AddEntitiesAction([entity]));

            ctx.dispatch([...actions, new SuccessAction(entity)]);
            return entity;
        } catch (e) {
            if (e instanceof HttpErrorResponse) ctx.dispatch(new FailureAction({ entityId: payload, e }));
            else throw new Error(e);
        } finally {
            this.setExpirationTime(payload);
        }
    }

    /**
     *
     * @param ctx
     * @param payload
     */
    protected _fetchEntitySuccess<M extends EntityStateModel<T>>({ getState, patchState }: StateContext<M>, { payload }: IActionWithPayload<T>): void {
        const state = getState();
        patchState({ fetchingItem: state.fetchingItem.filter((id) => id !== payload.id) } as Partial<M>);
    }

    /**
     *
     * @param ctx
     * @param payload
     */
    protected _fetchEntityFailure<M extends EntityStateModel<T>>(
        { getState, patchState }: StateContext<M>,
        { payload }: IActionWithPayload<IFetchEntityFailureActionPayload>
    ): void {
        const state = getState();
        patchState({ fetchingItem: state.fetchingItem.filter((id) => id !== payload.entityId) } as Partial<M>);
    }

    /**
     *
     * @param ctx
     * @param payload
     */
    protected _setEntities<M extends EntityStateModel<T>>({ getState, patchState }: StateContext<M>, { payload }: IActionWithPayload<T[]>): void {
        if (!payload) return;

        const state = getState();
        let mapHasChanged = !state.map || state.map.size !== payload.length;
        const stateMap = new Map(mapHasChanged ? null : state.map);

        payload
            .filter((entity) => !!entity)
            .forEach((entity) => {
                if (mapHasChanged || !stateMap.has(entity.id) || !MaxBrainUtils.areObjectsEqual(entity, stateMap.get(entity.id))) {
                    this.setExpirationTime(entity.id);
                    stateMap.set(entity.id, entity);
                    mapHasChanged = true;
                }
            });

        if (mapHasChanged) patchState({ map: stateMap } as Partial<M>);
    }

    /**
     *
     * @param ctx
     * @param SuccessAction
     * @param FailureAction
     * @param SetEntitiesAction
     * @param serviceMethod
     */
    protected async _fetchEntities<M extends EntityStateModel<T>>(
        ctx: StateContext<M>,
        SuccessAction: any,
        FailureAction: any,
        SetEntitiesAction: any,
        serviceMethod: Observable<T[]> = this.entityApiService ? this.entityApiService.getAll() : null
    ): Promise<void> {
        if (!serviceMethod) throw new Error('A service method must be provided to use the _fetchEntities protected method. ');

        const state = ctx.getState();
        if (state.fetchingList) return;

        ctx.patchState({ fetchingList: true } as Partial<M>);

        /**
         * Skips fetching the entities in case the timeoutDuration hasn't elapsed since the last fetch
         */
        if (this._expirationTime['list'] && this._expirationTime['list'] > Date.now() && state.map) {
            setTimeout(() => ctx.dispatch(new SuccessAction()));
            return;
        }

        try {
            const entities = await serviceMethod.toPromise();
            ctx.dispatch([new SetEntitiesAction(entities), new SuccessAction()]);
        } catch (e) {
            if (e instanceof HttpErrorResponse) ctx.dispatch(new FailureAction());
            else throw new Error(e);
        } finally {
            this.setExpirationTime('list');
        }
    }

    /**
     *
     * @param patchState
     */
    protected _fetchEntitiesSuccess<M extends EntityStateModel<T>>({ patchState }: StateContext<M>): void {
        patchState({ fetchingList: false } as Partial<M>);
    }

    /**
     *
     * @param patchState
     */
    protected _fetchEntitiesFailure<M extends EntityStateModel<T>>({ patchState }: StateContext<M>): void {
        patchState({ fetchingList: false } as Partial<M>);
    }

    /**
     *
     * @param ctx
     * @param entityIds
     */
    protected _removeEntitiesFromState<M extends EntityStateModel<T>>(ctx: StateContext<M>, entityIds: string | string[]): void {
        const state = ctx.getState();
        if (entityIds.includes(state.item)) this._unsetEntity(ctx);

        const payload = state.map ? Array.from(state.map.values()).filter((entity) => !entityIds.includes(entity.id)) : null;
        this._setEntities(ctx, { payload });
    }

    protected async _deleteEntities(ctx: StateContext<EntityStateModel<T>>, ids: string[], SuccessAction: any, FailureAction: any): Promise<void> {
        if (!this.entityApiService) {
            throw new Error('A entityService method must be provided in the constructor to use the _deleteEntities protected method. ');
        }

        let observable: Observable<string>;

        ids.forEach((entityId, index) => {
            observable = index === 0 ? this.entityApiService.delete(entityId) : merge(observable, this.entityApiService.delete(entityId));
        });

        try {
            const entityIds = await observable
                .pipe(
                    reduce<string, string[]>((acc, val) => {
                        if (!acc) {
                            return [val];
                        }

                        if (typeof acc === 'string') {
                            return [acc, val];
                        }

                        return val ? [...acc, val] : acc;
                    }),
                    takeLast(1)
                )
                .toPromise();

            this._removeEntitiesFromState(ctx, entityIds);

            ctx.dispatch(new SuccessAction({ entityIds }));
        } catch (e) {
            if (e instanceof HttpErrorResponse) {
                console.error(e);

                ctx.dispatch(new FailureAction());
            } else {
                throw new Error(e);
            }
        }
    }
}
